import type { StatBlock } from '@blogforge/shared';

// ─────────────────────────────────────────────────────────────────
// Canvas preview for the stat block — "big number + label" figures,
// e.g. a "by the numbers" section.
//
// Two things to know:
//
// 1. Blocks are never zod-parsed on the way in. BlockAdder builds them as
//    raw objects (`as never`) and EditorPage loads posts with a plain
//    JSON.parse, so the schema's `.default()`s (layout: 'row') are absent
//    at runtime. Every attr here is therefore treated as optional.
//
// 2. `animateOnScroll` is NOT implemented. The canvas is a static
//    preview, and published output does not render stat blocks at all
//    (themes/*/renderer.js covers only a handful of block types), so
//    there is nowhere the count-up could honestly run yet.
// ─────────────────────────────────────────────────────────────────

export function StatRenderer({ block }: { block: StatBlock }) {
  const stats = block.attrs?.stats ?? [];
  // 'grid' wraps into columns; 'row' keeps the figures on one line and
  // falls back to wrapping on narrow screens.
  const isGrid = block.attrs?.layout === 'grid';

  return (
    <div className="space-y-4">
      {block.content && <h3 className="doc-text text-lg font-semibold">{block.content}</h3>}

      {stats.length === 0 ? (
        <div className="border-line bg-bg-soft text-muted rounded-lg border border-dashed p-6 text-center text-sm">
          <p className="text-ink-soft font-medium">Stat block</p>
          <p className="text-muted mt-1 text-xs">
            Add stats in the block settings panel on the right
          </p>
        </div>
      ) : (
        <div
          className={isGrid ? 'grid gap-6 sm:grid-cols-2 lg:grid-cols-3' : 'flex flex-wrap gap-6'}
        >
          {stats.map((stat, index) => (
            <div key={index} className={isGrid ? 'min-w-0' : 'min-w-[8rem] flex-1'}>
              {stat.icon && <div className="mb-1 text-xl leading-none">{stat.icon}</div>}
              <div className="doc-text text-3xl font-bold tracking-tight">{stat.value}</div>
              {stat.label && <div className="doc-text mt-1 text-sm font-medium">{stat.label}</div>}
              {stat.description && <p className="doc-muted mt-1 text-sm">{stat.description}</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
